import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileUp,
  Filter,
  FolderPlus,
  Layers,
  Link2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Shield,
  SlidersHorizontal,
  TrendingUp,
  Users,
} from 'lucide-react';
import { POLICY_TYPES, PolicyType, PolicyRow } from '../types';
import { ConfirmModal, StatusBadge, btnPrimary, btnSecondary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, PolicyActivity, PolicyStats, GroupItem } from '../../../../components/api/aiGovernanceService';

const PAGE_SIZE = 8;

const POLICY_TYPE_COLORS: Record<string, string> = {
  'Data Protection': 'bg-blue-100 text-blue-600',
  'Access Control': 'bg-violet-100 text-violet-600',
  'Model Governance': 'bg-indigo-100 text-indigo-600',
  Compliance: 'bg-emerald-100 text-emerald-600',
  'Prompt Security': 'bg-rose-100 text-rose-600',
  'Runtime Enforcement': 'bg-cyan-100 text-cyan-600',
  'Tool Governance': 'bg-amber-100 text-amber-600',
};

function PolicyIcon({ type }: { type: PolicyType }) {
  const cls = POLICY_TYPE_COLORS[type] ?? 'bg-slate-100 text-slate-600';
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cls}`}>
      <Shield className="w-4 h-4" />
    </div>
  );
}

function DonutChart({ active, draft, inactive }: { active: number; draft: number; inactive: number }) {
  const total = active + draft + inactive || 1;
  const segments = [
    { value: active, color: '#10b981', label: 'Active' },
    { value: draft, color: '#f59e0b', label: 'Draft' },
    { value: inactive, color: '#94a3b8', label: 'Inactive' },
  ];
  let offset = 0;
  const r = 36;
  const c = 2 * Math.PI * r;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 96 96" className="w-full h-full -rotate-90">
          <circle cx="48" cy="48" r={r} fill="none" stroke="#f1f5f9" strokeWidth="12" />
          {segments.map((seg) => {
            const dash = (seg.value / total) * c;
            const el = (
              <circle
                key={seg.label}
                cx="48"
                cy="48"
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth="12"
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="round"
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-semibold text-slate-900">{total}</span>
          <span className="text-[9px] text-slate-500">Total</span>
        </div>
      </div>
      <div className="space-y-1.5 flex-1 min-w-0">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
              <span className="text-slate-600 truncate">{seg.label}</span>
            </div>
            <span className="font-medium text-slate-800 ml-2">{seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  icon: Icon,
  iconBg,
  trend,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  iconBg: string;
  trend?: { text: string; positive?: boolean };
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>
          <Icon className="w-4 h-4" />
        </div>
        {trend && (
          <span className={`text-[10px] font-medium flex items-center gap-0.5 ${trend.positive ? 'text-emerald-600' : 'text-slate-500'}`}>
            {trend.positive && <TrendingUp className="w-3 h-3" />}
            {trend.text}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-2">{label}</p>
      <p className="text-xl font-semibold text-slate-900 mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function PoliciesListing() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [groupFilter, setGroupFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [policies, setPolicies] = useState<PolicyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<PolicyStats | null>(null);
  const [activity, setActivity] = useState<PolicyActivity[]>([]);
  const [groups, setGroups] = useState<GroupItem[]>([]);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const selectedGroup = groups.find((g) => g.name === groupFilter);
      const result = await aiGovernanceService.getPolicies({
        search: search || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        type: typeFilter !== 'All' ? typeFilter : undefined,
        groupId: selectedGroup?.id,
        page,
        pageSize: PAGE_SIZE,
      });
      setPolicies(
        (result.items || []).map((p) => ({
          id: p.id,
          name: p.name,
          code: p.code,
          description: p.description,
          type: p.type as PolicyType,
          group: p.group,
          protectionLevel: p.protectionLevel as PolicyRow['protectionLevel'],
          status: p.status as PolicyRow['status'],
          assignedAssets: p.assignedAssets,
          lastUpdated: p.lastUpdated,
          lastUpdatedTime: p.lastUpdatedTime,
          riskLevel: p.riskLevel as PolicyRow['riskLevel'],
        }))
      );
      setTotal(result.total || 0);
    } catch {
      setError('Failed to load policies.');
      setPolicies([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, groupFilter, page, groups]);

  const fetchMeta = useCallback(async () => {
    try {
      const [statsRes, activityRes, groupsRes] = await Promise.all([
        aiGovernanceService.getPolicyStats(),
        aiGovernanceService.getPolicyActivity(5),
        aiGovernanceService.getGroups({ page: 1, pageSize: 50 }),
      ]);
      setStats(statsRes);
      setActivity(Array.isArray(activityRes) ? activityRes : []);
      setGroups(groupsRes.items || []);
    } catch {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const typeBarData = useMemo(() => {
    const items = stats?.byType || [];
    const max = Math.max(1, ...items.map((i) => i.count));
    const colors = ['bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-rose-500'];
    return items.slice(0, 5).map((item, i) => ({
      label: item.label,
      count: item.count,
      pct: Math.round((item.count / max) * 100),
      color: colors[i % colors.length],
    }));
  }, [stats]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await aiGovernanceService.deletePolicy(deleteTarget.id);
      setDeleteTarget(null);
      await Promise.all([fetchPolicies(), fetchMeta()]);
    } catch {
      setError('Failed to delete policy. It may still have assignments.');
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">AI Governance</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Manage and enforce guardrails, compliance, and responsible AI policies
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard label="Total Policies" value={stats?.total ?? '—'} icon={Shield} iconBg="bg-blue-50 text-blue-600" trend={stats ? { text: `${stats.trendVsLastMonthPct}% vs last month`, positive: true } : undefined} />
        <MetricCard label="Active Policies" value={stats?.active ?? '—'} sub={stats ? `${Math.round(((stats.active || 0) / Math.max(1, stats.total)) * 100)}% of total` : undefined} icon={Layers} iconBg="bg-emerald-50 text-emerald-600" />
        <MetricCard label="Groups" value={stats?.groups ?? '—'} sub={stats ? `${stats.unassignedGroups} unassigned` : undefined} icon={Users} iconBg="bg-violet-50 text-violet-600" />
        <MetricCard label="Assignments" value={stats?.assignments ?? '—'} sub="Across all assets" icon={Link2} iconBg="bg-cyan-50 text-cyan-600" />
        <MetricCard label="Overrides" value={stats?.overrides ?? '—'} sub="Require attention" icon={AlertTriangle} iconBg="bg-rose-50 text-rose-600" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4 items-start">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-900">Policies</h2>
            <div className="flex items-center gap-2">
              <button className={btnSecondary}><FileUp className="w-3.5 h-3.5" /> Import Policy</button>
              <button onClick={() => navigate('/portal/ai-governance/policies/create')} className={btnPrimary}>
                <Plus className="w-3.5 h-3.5" /> Create Policy
              </button>
            </div>
          </div>

          <div className="px-4 py-3 border-b border-slate-100 flex flex-col lg:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search policies by name..."
                className={`${inputClass} pl-9 h-9`}
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className={`${selectClass} h-9 lg:w-28`}>
              <option value="All">Status</option>
              <option>Active</option>
              <option>Draft</option>
              <option>Inactive</option>
            </select>
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className={`${selectClass} h-9 lg:w-36`}>
              <option value="All">Type</option>
              {POLICY_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={groupFilter} onChange={(e) => { setGroupFilter(e.target.value); setPage(1); }} className={`${selectClass} h-9 lg:w-32`}>
              <option value="All">Group</option>
              {groups.map((g) => <option key={g.id}>{g.name}</option>)}
            </select>
            <button className={`${btnSecondary} h-9`}><Filter className="w-3.5 h-3.5" /> Filters</button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  {['Policy Name', 'Type', 'Group', 'Status', 'Coverage', 'Last Updated', ''].map((h) => (
                    <th key={h || 'actions'} className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500 text-sm">Loading policies...</td></tr>
                ) : policies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500 text-sm">No policies match your filters.</td>
                  </tr>
                ) : (
                  policies.map((policy) => (
                    <tr key={policy.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <PolicyIcon type={policy.type} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 text-[13px]">{policy.name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{policy.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{policy.type}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{policy.group}</td>
                      <td className="px-4 py-3"><StatusBadge status={policy.status} /></td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{policy.assignedAssets} Assets</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-slate-700">{policy.lastUpdated}</p>
                        {policy.lastUpdatedTime && <p className="text-[10px] text-slate-400">{policy.lastUpdatedTime}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            onClick={() => navigate(`/portal/ai-governance/policies/${policy.id}/edit`)}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget({ id: policy.id, name: policy.name })}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                            title="Delete"
                          >
                            <MoreHorizontal className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-[11px] text-slate-500">
              Showing {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} results
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 8).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`min-w-[28px] h-7 px-2 rounded-md text-[11px] font-medium transition-colors ${
                    page === p ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Groups</h3>
              <button onClick={() => navigate('/portal/ai-governance/groups')} className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                <FolderPlus className="w-3 h-3" /> New Group
              </button>
            </div>
            <div className="divide-y divide-slate-100">
              {groups.slice(0, 6).map((g) => (
                <button
                  key={g.id}
                  onClick={() => navigate('/portal/ai-governance/groups')}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                >
                  <div>
                    <p className="text-[13px] font-medium text-slate-800">{g.name}</p>
                    <p className="text-[10px] text-slate-500">{g.policyCount} policies · {g.assignedAssets} assets</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
              ))}
              {groups.length === 0 && <p className="px-4 py-3 text-[11px] text-slate-500">No groups yet.</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Policy Summary</h3>
            <DonutChart active={stats?.active || 0} draft={stats?.draft || 0} inactive={stats?.inactive || 0} />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Top Policy Types</h3>
            <div className="space-y-2.5">
              {typeBarData.length === 0 ? (
                <p className="text-[11px] text-slate-500">No type data yet.</p>
              ) : typeBarData.map((item) => (
                <div key={item.label}>
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="text-slate-600">{item.label}</span>
                    <span className="font-medium text-slate-800">{item.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Quick Actions</h3>
            <div className="space-y-1">
              <button onClick={() => navigate('/portal/ai-governance/policies/create')} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-[12px] text-slate-700 hover:bg-slate-50 text-left">
                <Plus className="w-3.5 h-3.5 text-indigo-600" /> Create New Policy
              </button>
              <button onClick={() => navigate('/portal/ai-governance/groups')} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-[12px] text-slate-700 hover:bg-slate-50 text-left">
                <Users className="w-3.5 h-3.5 text-violet-600" /> Create New Group
              </button>
              <button onClick={() => navigate('/portal/ai-governance/assignments')} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-[12px] text-slate-700 hover:bg-slate-50 text-left">
                <Link2 className="w-3.5 h-3.5 text-cyan-600" /> Assign Policy
              </button>
              <button onClick={() => navigate('/portal/ai-governance/audit-logs')} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-[12px] text-slate-700 hover:bg-slate-50 text-left">
                <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" /> View Audit Logs
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Recent Policy Activity</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {activity.length === 0 ? (
            <p className="px-4 py-3 text-[11px] text-slate-500">No recent activity.</p>
          ) : activity.map((item) => (
            <div key={item.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50/50">
              <span className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-blue-500" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-800">{item.type}</p>
                <p className="text-[11px] text-slate-500">{item.description}</p>
              </div>
              <span className="text-[11px] text-slate-400 whitespace-nowrap">{item.timestamp}</span>
            </div>
          ))}
        </div>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Policy"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          confirmLabel="Delete Policy"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
